import { useEffect, useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { AppLayout } from "@/components/AppLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import {
  apiGetMyPerformance,
  apiRequestGoalMeeting,
  apiRequestCareerMeeting,
  apiGetCareerMeetingStatus,
  apiGetMyCareerPath,
  apiCreateCareerPath,
  apiUpdateCareerPath,
  apiDeleteCareerPath,
  type PerformanceData,
  type PerformanceKpi,
  type PerformanceGoal,
  type PerformanceReview,
  type CareerPath,
  type CareerMeetingTopic,
  type CareerMeetingStatus,
} from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  TrendingUp, Target, Star, Award, BarChart3,
  Calendar, User, CheckCircle2, Clock, AlertTriangle,
  ChevronRight, Sparkles, Trophy, CalendarPlus,
  GraduationCap, ArrowUpCircle, Pencil, Trash2, Plus, BookOpen, Lightbulb, CalendarClock,
} from "lucide-react";
import {
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip,
  BarChart, Bar, Cell, CartesianGrid,
} from "recharts";

const ratingColorMap: Record<string, string> = {
  Outstanding: "bg-amber-100 text-amber-700 border-amber-200",
  "Exceeds Expectations": "bg-emerald-100 text-emerald-700 border-emerald-200",
  "Meets Expectations": "bg-blue-100 text-blue-700 border-blue-200",
  "Needs Improvement": "bg-orange-100 text-orange-700 border-orange-200",
  Unsatisfactory: "bg-red-100 text-red-700 border-red-200",
};

const goalStatusConfig: Record<string, { icon: typeof CheckCircle2; color: string; bg: string }> = {
  "Not Started": { icon: Clock, color: "text-gray-500", bg: "bg-gray-100 text-gray-600 border-gray-200" },
  "In Progress": { icon: TrendingUp, color: "text-blue-500", bg: "bg-blue-100 text-blue-600 border-blue-200" },
  Completed: { icon: CheckCircle2, color: "text-emerald-500", bg: "bg-emerald-100 text-emerald-600 border-emerald-200" },
  Overdue: { icon: AlertTriangle, color: "text-red-500", bg: "bg-red-100 text-red-600 border-red-200" },
};

const CHART_COLORS = ["hsl(var(--primary))", "hsl(var(--success))", "hsl(var(--info))", "hsl(var(--warning))", "#8b5cf6", "#ec4899"];

const Performance = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { toast } = useToast();
  const [data, setData] = useState<PerformanceData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [animatedValues, setAnimatedValues] = useState({ score: 0, kpis: 0, goals: 0, reviews: 0 });
  const queryClient = useQueryClient();

  // Fetch which topics already have a pending (not yet completed) task
  const { data: meetingStatus = { kpis: false, goals: false, reviews: false, career_path: false } } =
    useQuery<CareerMeetingStatus>({
      queryKey: ["career-meeting-status"],
      queryFn: apiGetCareerMeetingStatus,
      staleTime: 30_000,
    });

  const [submittingTopic, setSubmittingTopic] = useState<CareerMeetingTopic | null>(null);

  const careerMeetingMutation = useMutation({
    mutationFn: (topic: CareerMeetingTopic) => apiRequestCareerMeeting(topic),
    onMutate: (topic) => setSubmittingTopic(topic),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ["career-meeting-status"] });
      toast({
        title: "Meeting request sent",
        description: `Your request has been sent to ${res.targetName}. They'll see it in their task inbox.`,
      });
    },
    onError: (err: any) => {
      toast({
        title: "Request failed",
        description: err.message?.includes("already pending")
          ? "A meeting request for this topic is already pending."
          : "Something went wrong. Please try again.",
        variant: "destructive",
      });
    },
    onSettled: () => setSubmittingTopic(null),
  });

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    apiGetMyPerformance()
      .then((d) => {
        if (!cancelled) {
          setData(d);
          setError(null);
        }
      })
      .catch((err) => {
        if (!cancelled) setError(err.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  // Animated counter effect
  useEffect(() => {
    if (!data) return;
    const avgRating = data.reviews.length > 0
      ? data.reviews.reduce((s, r) => s + r.overallRating, 0) / data.reviews.length
      : 0;
    const completedGoals = data.goals.filter((g) => g.status === "Completed").length;

    const targets = { score: Math.round(avgRating * 20), kpis: data.kpis.length, goals: completedGoals, reviews: data.reviews.length };
    const duration = 800;
    const steps = 30;
    const interval = duration / steps;
    let step = 0;

    const timer = setInterval(() => {
      step++;
      const progress = Math.min(step / steps, 1);
      const ease = 1 - Math.pow(1 - progress, 3);
      setAnimatedValues({
        score: Math.round(targets.score * ease),
        kpis: Math.round(targets.kpis * ease),
        goals: Math.round(targets.goals * ease),
        reviews: Math.round(targets.reviews * ease),
      });
      if (step >= steps) clearInterval(timer);
    }, interval);

    return () => clearInterval(timer);
  }, [data]);

  // KPI radar chart data
  const radarData = useMemo(() => {
    if (!data) return [];
    return data.kpis.map((k) => ({
      name: k.title.length > 15 ? k.title.slice(0, 15) + "…" : k.title,
      value: k.targetValue > 0 ? Math.round((k.currentValue / k.targetValue) * 100) : 0,
      fullMark: 100,
    }));
  }, [data]);

  // Review rating trend for area chart
  const ratingTrendData = useMemo(() => {
    if (!data) return [];
    return [...data.reviews]
      .sort((a, b) => new Date(a.reviewDate).getTime() - new Date(b.reviewDate).getTime())
      .map((r) => ({
        period: r.reviewPeriod,
        rating: r.overallRating,
      }));
  }, [data]);

  // Goal distribution for bar chart
  const goalDistribution = useMemo(() => {
    if (!data) return [];
    const counts: Record<string, number> = { "Not Started": 0, "In Progress": 0, Completed: 0, Overdue: 0 };
    data.goals.forEach((g) => { counts[g.status] = (counts[g.status] || 0) + 1; });
    return Object.entries(counts)
      .filter(([, v]) => v > 0)
      .map(([status, count]) => ({ status, count }));
  }, [data]);

  const avgRating = data && data.reviews.length > 0
    ? (data.reviews.reduce((s, r) => s + r.overallRating, 0) / data.reviews.length).toFixed(1)
    : "—";

  const completedGoals = data ? data.goals.filter((g) => g.status === "Completed").length : 0;
  const totalGoals = data ? data.goals.length : 0;

  if (loading) {
    return (
      <AppLayout>
        <div className="space-y-6 w-full animate-pulse">
          <div className="h-8 w-64 bg-muted rounded" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-24 bg-muted rounded-xl" />
            ))}
          </div>
          <div className="h-96 bg-muted rounded-xl" />
        </div>
      </AppLayout>
    );
  }

  if (error) {
    return (
      <AppLayout>
        <div className="flex flex-col items-center justify-center min-h-[400px] space-y-4">
          <div className="h-16 w-16 rounded-full bg-red-100 flex items-center justify-center">
            <AlertTriangle className="h-8 w-8 text-red-500" />
          </div>
          <p className="text-muted-foreground">{error}</p>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="space-y-6 w-full">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 animate-in fade-in slide-in-from-left-5 duration-500">
            <div className="h-9 w-9 rounded-xl bg-violet-500/10 flex items-center justify-center shrink-0">
              <TrendingUp className="h-5 w-5 text-violet-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-foreground">
                {t("performance.title")}
              </h1>
              <p className="text-sm text-muted-foreground mt-1">
                {t("performance.subtitle")}
              </p>
            </div>
          </div>
        </div>

        {/* Stats strip */}
        <div className="rounded-2xl border border-border/60 bg-card shadow-sm overflow-hidden animate-in fade-in slide-in-from-bottom-4 duration-500">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 sm:divide-x divide-border/60 divide-y sm:divide-y-0 lg:divide-y-0">
            <div className="flex items-center gap-3.5 px-5 py-4">
              <div className="h-9 w-9 rounded-xl bg-amber-500/10 flex items-center justify-center shrink-0">
                <Trophy className="h-4 w-4 text-amber-600" />
              </div>
              <div className="min-w-0">
                <p className="text-2xl font-bold text-foreground leading-none tabular-nums">
                  {avgRating !== "—" ? `${animatedValues.score}%` : "—"}
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">{t("performance.overallScore")}</p>
              </div>
            </div>
            <div className="flex items-center gap-3.5 px-5 py-4">
              <div className="h-9 w-9 rounded-xl bg-blue-500/10 flex items-center justify-center shrink-0">
                <Target className="h-4 w-4 text-blue-600" />
              </div>
              <div className="min-w-0">
                <p className="text-2xl font-bold text-foreground leading-none tabular-nums">{animatedValues.kpis}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{t("performance.activeKpis")}</p>
              </div>
            </div>
            <div className="flex items-center gap-3.5 px-5 py-4">
              <div className="h-9 w-9 rounded-xl bg-emerald-500/10 flex items-center justify-center shrink-0">
                <CheckCircle2 className="h-4 w-4 text-emerald-600" />
              </div>
              <div className="min-w-0">
                <p className="text-2xl font-bold text-foreground leading-none tabular-nums">{animatedValues.goals}/{totalGoals}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{t("performance.goalsCompleted")}</p>
              </div>
            </div>
            <div className="flex items-center gap-3.5 px-5 py-4">
              <div className="h-9 w-9 rounded-xl bg-violet-500/10 flex items-center justify-center shrink-0">
                <Star className="h-4 w-4 text-violet-600" />
              </div>
              <div className="min-w-0">
                <p className="text-2xl font-bold text-foreground leading-none tabular-nums">{animatedValues.reviews}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{t("performance.totalReviews")}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="overview" className="animate-in fade-in slide-in-from-bottom-4 duration-500 delay-300">
          <TabsList className="w-full justify-start bg-transparent h-auto p-0 border-b border-border/60 rounded-none gap-0 overflow-x-auto overflow-y-hidden">
            <TabsTrigger
              value="overview"
              className="flex items-center gap-1.5 text-sm rounded-none border-b-2 border-transparent px-4 py-2.5 -mb-px data-[state=active]:border-primary data-[state=active]:text-foreground data-[state=active]:shadow-none text-muted-foreground hover:text-foreground transition-colors bg-transparent"
            >
              <BarChart3 className="h-3.5 w-3.5" />
              {t("performance.overviewTab")}
            </TabsTrigger>
            <TabsTrigger
              value="kpis"
              className="flex items-center gap-1.5 text-sm rounded-none border-b-2 border-transparent px-4 py-2.5 -mb-px data-[state=active]:border-primary data-[state=active]:text-foreground data-[state=active]:shadow-none text-muted-foreground hover:text-foreground transition-colors bg-transparent"
            >
              <Target className="h-3.5 w-3.5" />
              {t("performance.kpisTab")}
            </TabsTrigger>
            <TabsTrigger
              value="goals"
              className="flex items-center gap-1.5 text-sm rounded-none border-b-2 border-transparent px-4 py-2.5 -mb-px data-[state=active]:border-primary data-[state=active]:text-foreground data-[state=active]:shadow-none text-muted-foreground hover:text-foreground transition-colors bg-transparent"
            >
              <TrendingUp className="h-3.5 w-3.5" />
              {t("performance.goalsTab")}
            </TabsTrigger>
            <TabsTrigger
              value="reviews"
              className="flex items-center gap-1.5 text-sm rounded-none border-b-2 border-transparent px-4 py-2.5 -mb-px data-[state=active]:border-primary data-[state=active]:text-foreground data-[state=active]:shadow-none text-muted-foreground hover:text-foreground transition-colors bg-transparent"
            >
              <Star className="h-3.5 w-3.5" />
              {t("performance.reviewsTab")}
            </TabsTrigger>
            <TabsTrigger
              value="career-path"
              className="flex items-center gap-1.5 text-sm rounded-none border-b-2 border-transparent px-4 py-2.5 -mb-px data-[state=active]:border-primary data-[state=active]:text-foreground data-[state=active]:shadow-none text-muted-foreground hover:text-foreground transition-colors bg-transparent"
            >
              <GraduationCap className="h-3.5 w-3.5" />
              {t("performance.careerPathTab")}
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6 mt-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* KPI Radar */}
              {radarData.length > 0 && (
                <Card className="shadow-card animate-in fade-in slide-in-from-left-4 duration-500">
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <BarChart3 className="h-4 w-4 text-primary" />
                      {t("performance.kpiCompletion")}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={280}>
                      <RadarChart data={radarData}>
                        <PolarGrid stroke="hsl(var(--border))" />
                        <PolarAngleAxis dataKey="name" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
                        <PolarRadiusAxis angle={90} domain={[0, 100]} tick={false} axisLine={false} />
                        <Radar
                          name="KPI"
                          dataKey="value"
                          stroke="hsl(var(--primary))"
                          fill="hsl(var(--primary))"
                          fillOpacity={0.2}
                          strokeWidth={2}
                          animationDuration={1200}
                          animationEasing="ease-out"
                        />
                      </RadarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              )}

              {/* Rating Trend */}
              {ratingTrendData.length > 0 && (
                <Card className="shadow-card animate-in fade-in slide-in-from-right-4 duration-500">
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <TrendingUp className="h-4 w-4 text-emerald-500" />
                      {t("performance.ratingTrend")}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={280}>
                      <AreaChart data={ratingTrendData}>
                        <defs>
                          <linearGradient id="ratingGradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.3} />
                            <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                        <XAxis dataKey="period" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
                        <YAxis domain={[0, 5]} tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
                        <Tooltip
                          contentStyle={{
                            borderRadius: "8px",
                            border: "1px solid hsl(var(--border))",
                            backgroundColor: "hsl(var(--card))",
                            fontSize: 12,
                          }}
                        />
                        <Area
                          type="monotone"
                          dataKey="rating"
                          stroke="hsl(var(--primary))"
                          fill="url(#ratingGradient)"
                          strokeWidth={2.5}
                          dot={{ r: 5, fill: "hsl(var(--primary))", strokeWidth: 2, stroke: "white" }}
                          animationDuration={1500}
                          animationEasing="ease-out"
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              )}

              {/* Goal Distribution */}
              {goalDistribution.length > 0 && (
                <Card className="shadow-card animate-in fade-in slide-in-from-left-4 duration-500 delay-150">
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Target className="h-4 w-4 text-blue-500" />
                      {t("performance.goalProgress")}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={280}>
                      <BarChart data={goalDistribution} layout="vertical" barCategoryGap="20%">
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" horizontal={false} />
                        <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
                        <YAxis type="category" dataKey="status" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} width={90} />
                        <Tooltip
                          contentStyle={{
                            borderRadius: "8px",
                            border: "1px solid hsl(var(--border))",
                            backgroundColor: "hsl(var(--card))",
                            fontSize: 12,
                          }}
                        />
                        <Bar dataKey="count" radius={[0, 6, 6, 0]} animationDuration={1200} animationEasing="ease-out">
                          {goalDistribution.map((_, idx) => (
                            <Cell key={idx} fill={CHART_COLORS[idx % CHART_COLORS.length]} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              )}

              {/* Latest Review Card */}
              {data && data.reviews.length > 0 && (
                <Card className="shadow-card animate-in fade-in slide-in-from-right-4 duration-500 delay-150">
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Award className="h-4 w-4 text-amber-500" />
                      Latest Review
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <LatestReviewCard review={data.reviews[0]} t={t} />
                  </CardContent>
                </Card>
              )}

              {/* Empty state for overview */}
              {data && radarData.length === 0 && ratingTrendData.length === 0 && goalDistribution.length === 0 && (
                <Card className="shadow-card col-span-full">
                  <CardContent className="flex flex-col items-center justify-center py-16 space-y-4">
                    <div className="h-20 w-20 rounded-full bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center animate-pulse">
                      <Sparkles className="h-10 w-10 text-primary" />
                    </div>
                    <div className="text-center space-y-1">
                      <p className="font-medium text-foreground">No performance data yet</p>
                      <p className="text-sm text-muted-foreground max-w-md">
                        Your manager or HR will assign KPIs, goals, and reviews. They'll appear here once available.
                      </p>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* KPIs Tab */}
          <TabsContent value="kpis" className="space-y-4 mt-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-foreground">{t("performance.kpiTitle")}</h2>
                <p className="text-sm text-muted-foreground">{t("performance.kpiSubtitle")}</p>
              </div>
            </div>

            {data && data.kpis.length === 0 ? (
              <EmptyState
                icon={Target}
                title={t("performance.noKpis")}
                description={t("performance.noKpisDesc")}
                onRequestMeeting={() => careerMeetingMutation.mutate("kpis")}
                isSending={submittingTopic === "kpis"}
                hasPendingRequest={meetingStatus.kpis}
              />
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {data?.kpis.map((kpi, idx) => (
                  <KpiCard key={kpi.id} kpi={kpi} index={idx} t={t} />
                ))}
              </div>
            )}
          </TabsContent>

          {/* Goals Tab */}
          <TabsContent value="goals" className="space-y-4 mt-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-foreground">{t("performance.goalsTitle")}</h2>
                <p className="text-sm text-muted-foreground">{t("performance.goalsSubtitle")}</p>
              </div>
            </div>

            {data && data.goals.length === 0 ? (
              <EmptyState
                icon={CheckCircle2}
                title={t("performance.noGoals")}
                description={t("performance.noGoalsDesc")}
                onRequestMeeting={() => careerMeetingMutation.mutate("goals")}
                isSending={submittingTopic === "goals"}
                hasPendingRequest={meetingStatus.goals}
              />
            ) : (
              <div className="space-y-3">
                {data?.goals.map((goal, idx) => (
                  <GoalCard key={goal.id} goal={goal} index={idx} t={t} />
                ))}
              </div>
            )}
          </TabsContent>

          {/* Reviews Tab */}
          <TabsContent value="reviews" className="space-y-4 mt-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-foreground">{t("performance.reviewsTitle")}</h2>
                <p className="text-sm text-muted-foreground">{t("performance.reviewsSubtitle")}</p>
              </div>
            </div>

            {data && data.reviews.length === 0 ? (
              <EmptyState
                icon={Star}
                title={t("performance.noReviews")}
                description={t("performance.noReviewsDesc")}
                onRequestMeeting={() => careerMeetingMutation.mutate("reviews")}
                isSending={submittingTopic === "reviews"}
                hasPendingRequest={meetingStatus.reviews}
              />
            ) : (
              <div className="space-y-4">
                {data?.reviews.map((review, idx) => (
                  <ReviewCard key={review.id} review={review} index={idx} t={t} />
                ))}
              </div>
            )}
          </TabsContent>

          {/* Career Path Tab */}
          <TabsContent value="career-path" className="mt-6">
            <CareerPathTab
              employeeId={data?.employeeId ?? null}
              onRequestMeeting={() => careerMeetingMutation.mutate("career_path")}
              isSending={submittingTopic === "career_path"}
              hasPendingRequest={meetingStatus.career_path}
            />
          </TabsContent>
        </Tabs>
      </div>
    </AppLayout>
  );
};

// ============================================================
// Sub-components
// ============================================================

function KpiCard({ kpi, index, t }: { kpi: PerformanceKpi; index: number; t: any }) {
  const percentage = kpi.targetValue > 0 ? Math.round((kpi.currentValue / kpi.targetValue) * 100) : 0;
  const [animatedProgress, setAnimatedProgress] = useState(0);

  useEffect(() => {
    const timer = setTimeout(() => setAnimatedProgress(percentage), 100 + index * 80);
    return () => clearTimeout(timer);
  }, [percentage, index]);

  return (
    <Card
      className={`shadow-card hover:shadow-lg transition-all duration-300 animate-in fade-in slide-in-from-bottom-4 duration-500`}
      style={{ animationDelay: `${index * 80}ms` }}
    >
      <CardContent className="p-5 space-y-4">
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-foreground truncate">{kpi.title}</h3>
            {kpi.description && (
              <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{kpi.description}</p>
            )}
          </div>
          <Badge variant="outline" className="ml-2 shrink-0 text-[10px]">
            {kpi.period}
          </Badge>
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">
              {kpi.currentValue}{kpi.unit} / {kpi.targetValue}{kpi.unit}
            </span>
            <span className={`font-bold tabular-nums ${percentage >= 80 ? "text-emerald-600" : percentage >= 50 ? "text-blue-600" : "text-orange-600"}`}>
              {percentage}%
            </span>
          </div>
          <div className="relative h-2.5 rounded-full bg-muted overflow-hidden">
            <div
              className={`absolute inset-y-0 left-0 rounded-full transition-all duration-1000 ease-out ${
                percentage >= 80 ? "bg-gradient-to-r from-emerald-500 to-green-400" :
                percentage >= 50 ? "bg-gradient-to-r from-blue-500 to-cyan-400" :
                "bg-gradient-to-r from-orange-500 to-amber-400"
              }`}
              style={{ width: `${animatedProgress}%` }}
            />
          </div>
        </div>

        <div className="flex items-center justify-between text-xs text-muted-foreground pt-1">
          <span className="flex items-center gap-1">
            <BarChart3 className="h-3 w-3" />
            {t("performance.weight")}: {kpi.weight}
          </span>
          {kpi.createdByName && (
            <span className="flex items-center gap-1">
              <User className="h-3 w-3" />
              {kpi.createdByName}
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function GoalCard({ goal, index, t }: { goal: PerformanceGoal; index: number; t: any }) {
  const config = goalStatusConfig[goal.status] || goalStatusConfig["Not Started"];
  const StatusIcon = config.icon;
  const [animatedProgress, setAnimatedProgress] = useState(0);
  const [requested, setRequested] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    const timer = setTimeout(() => setAnimatedProgress(goal.progress), 100 + index * 80);
    return () => clearTimeout(timer);
  }, [goal.progress, index]);

  const meetingMutation = useMutation({
    mutationFn: () => apiRequestGoalMeeting(Number(goal.id)),
    onSuccess: () => {
      setRequested(true);
      toast({ title: "Meeting requested", description: "Your request has been sent." });
    },
    onError: (err: any) => {
      toast({ title: "Could not send request", description: err.message, variant: "destructive" });
    },
  });

  return (
    <Card
      className="shadow-card hover:shadow-lg transition-all duration-300 animate-in fade-in slide-in-from-bottom-4 duration-500"
      style={{ animationDelay: `${index * 80}ms` }}
    >
      <CardContent className="p-5">
        <div className="flex items-start gap-4">
          <div className={`h-10 w-10 rounded-xl flex items-center justify-center shrink-0 ${config.color} bg-opacity-10 ${goal.status === "Completed" ? "bg-emerald-100" : goal.status === "Overdue" ? "bg-red-100" : goal.status === "In Progress" ? "bg-blue-100" : "bg-gray-100"}`}>
            <StatusIcon className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-0 space-y-3">
            <div className="flex items-start justify-between gap-2">
              <div>
                <h3 className="font-semibold text-foreground">{goal.title}</h3>
                {goal.description && (
                  <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{goal.description}</p>
                )}
              </div>
              <Badge variant="outline" className={`shrink-0 text-[10px] border ${config.bg}`}>
                {goal.status}
              </Badge>
            </div>

            <div className="space-y-1.5">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">{t("performance.progress")}</span>
                <span className="font-semibold tabular-nums">{goal.progress}%</span>
              </div>
              <div className="relative h-2 rounded-full bg-muted overflow-hidden">
                <div
                  className={`absolute inset-y-0 left-0 rounded-full transition-all duration-1000 ease-out ${
                    goal.status === "Completed" ? "bg-gradient-to-r from-emerald-500 to-green-400" :
                    goal.status === "Overdue" ? "bg-gradient-to-r from-red-500 to-rose-400" :
                    "bg-gradient-to-r from-blue-500 to-cyan-400"
                  }`}
                  style={{ width: `${animatedProgress}%` }}
                />
              </div>
            </div>

            <div className="flex items-center gap-4 text-xs text-muted-foreground">
              {goal.dueDate && (
                <span className="flex items-center gap-1">
                  <Calendar className="h-3 w-3" />
                  {new Date(goal.dueDate).toLocaleDateString()}
                </span>
              )}
              {goal.category && (
                <Badge variant="secondary" className="text-[10px] h-5">
                  {goal.category}
                </Badge>
              )}
              {goal.createdByName && (
                <span className="flex items-center gap-1 ml-auto">
                  <User className="h-3 w-3" />
                  {goal.createdByName}
                </span>
              )}
            </div>

            {goal.createdByName && (
              <div className="flex justify-end pt-1">
                <Button
                  size="sm"
                  variant="outline"
                  className="text-xs h-7 gap-1.5"
                  disabled={requested || meetingMutation.isPending}
                  onClick={() => meetingMutation.mutate()}
                >
                  <CalendarPlus className="h-3 w-3" />
                  {requested ? "Request Sent" : "Request Meeting"}
                </Button>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function ReviewCard({ review, index, t }: { review: PerformanceReview; index: number; t: any }) {
  const [expanded, setExpanded] = useState(false);
  const ratingPercent = (review.overallRating / 5) * 100;

  return (
    <Card
      className="shadow-card hover:shadow-lg transition-all duration-300 animate-in fade-in slide-in-from-bottom-4 duration-500 cursor-pointer"
      style={{ animationDelay: `${index * 100}ms` }}
      onClick={() => setExpanded(!expanded)}
    >
      <CardContent className="p-5">
        <div className="flex items-start gap-4">
          <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-md shrink-0">
            <Star className="h-6 w-6 text-white" />
          </div>
          <div className="flex-1 min-w-0 space-y-2">
            <div className="flex items-start justify-between gap-2">
              <div>
                <h3 className="font-semibold text-foreground">{review.reviewPeriod}</h3>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-sm text-muted-foreground flex items-center gap-1">
                    <User className="h-3 w-3" />
                    {review.reviewerName || "—"}
                  </span>
                  <span className="text-xs text-muted-foreground">•</span>
                  <span className="text-xs text-muted-foreground">
                    {new Date(review.reviewDate).toLocaleDateString()}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Badge variant="outline" className={`text-xs border ${ratingColorMap[review.ratingLabel] || ""}`}>
                  {review.ratingLabel}
                </Badge>
                <div className="flex items-center gap-1">
                  <span className="text-xl font-bold text-foreground tabular-nums">{review.overallRating.toFixed(1)}</span>
                  <span className="text-xs text-muted-foreground">/5</span>
                </div>
              </div>
            </div>

            {/* Rating bar */}
            <div className="relative h-1.5 rounded-full bg-muted overflow-hidden">
              <div
                className="absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-amber-400 to-orange-500 transition-all duration-1000 ease-out"
                style={{ width: `${ratingPercent}%` }}
              />
            </div>

            {/* Expandable details */}
            <div className={`overflow-hidden transition-all duration-300 ${expanded ? "max-h-96 opacity-100 mt-3" : "max-h-0 opacity-0"}`}>
              <div className="space-y-3 pt-3 border-t border-border">
                {review.strengths && (
                  <div>
                    <p className="text-xs font-semibold text-emerald-600 mb-1">{t("performance.strengths")}</p>
                    <p className="text-sm text-muted-foreground">{review.strengths}</p>
                  </div>
                )}
                {review.improvements && (
                  <div>
                    <p className="text-xs font-semibold text-orange-600 mb-1">{t("performance.improvements")}</p>
                    <p className="text-sm text-muted-foreground">{review.improvements}</p>
                  </div>
                )}
                {review.comments && (
                  <div>
                    <p className="text-xs font-semibold text-blue-600 mb-1">{t("performance.comments")}</p>
                    <p className="text-sm text-muted-foreground">{review.comments}</p>
                  </div>
                )}
              </div>
            </div>

            <div className="flex items-center text-xs text-muted-foreground pt-1">
              <ChevronRight className={`h-3 w-3 transition-transform duration-300 ${expanded ? "rotate-90" : ""}`} />
              <span className="ml-1">{expanded ? "Click to collapse" : "Click to expand"}</span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function LatestReviewCard({ review, t }: { review: PerformanceReview; t: any }) {
  const stars = Array.from({ length: 5 }, (_, i) => i < Math.round(review.overallRating));

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="font-semibold text-foreground">{review.reviewPeriod}</p>
          <p className="text-xs text-muted-foreground mt-0.5">
            {review.reviewerName} • {new Date(review.reviewDate).toLocaleDateString()}
          </p>
        </div>
        <Badge variant="outline" className={`text-xs border ${ratingColorMap[review.ratingLabel] || ""}`}>
          {review.ratingLabel}
        </Badge>
      </div>

      <div className="flex items-center gap-1.5">
        {stars.map((filled, i) => (
          <Star
            key={i}
            className={`h-6 w-6 transition-all duration-300 ${
              filled
                ? "text-amber-400 fill-amber-400 scale-110"
                : "text-gray-200"
            }`}
            style={{ animationDelay: `${i * 100}ms` }}
          />
        ))}
        <span className="ml-2 text-xl font-bold tabular-nums">{review.overallRating.toFixed(1)}</span>
      </div>

      {review.strengths && (
        <div className="p-3 rounded-lg bg-emerald-50 dark:bg-emerald-500/10">
          <p className="text-xs font-semibold text-emerald-700 dark:text-emerald-400 mb-1">{t("performance.strengths")}</p>
          <p className="text-sm text-emerald-600 dark:text-emerald-300 line-clamp-3">{review.strengths}</p>
        </div>
      )}

      {review.improvements && (
        <div className="p-3 rounded-lg bg-orange-50 dark:bg-orange-500/10">
          <p className="text-xs font-semibold text-orange-700 dark:text-orange-400 mb-1">{t("performance.improvements")}</p>
          <p className="text-sm text-orange-600 dark:text-orange-300 line-clamp-3">{review.improvements}</p>
        </div>
      )}
    </div>
  );
}

function EmptyState({
  icon: Icon, title, description,
  onRequestMeeting, isSending, hasPendingRequest,
}: {
  icon: typeof Target;
  title: string;
  description: string;
  onRequestMeeting?: () => void;
  isSending?: boolean;
  hasPendingRequest?: boolean;
}) {
  return (
    <Card className="shadow-card">
      <CardContent className="flex flex-col items-center justify-center py-16 space-y-4">
        <div className="h-16 w-16 rounded-full bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center animate-bounce-gentle">
          <Icon className="h-8 w-8 text-primary/60" />
        </div>
        <div className="text-center space-y-1">
          <p className="font-medium text-foreground">{title}</p>
          <p className="text-sm text-muted-foreground max-w-sm">{description}</p>
        </div>
        {onRequestMeeting && (
          <Button
            variant="outline"
            size="sm"
            onClick={onRequestMeeting}
            disabled={isSending || hasPendingRequest}
            className="mt-2 gap-2"
          >
            <CalendarClock className="h-4 w-4" />
            {isSending
              ? "Sending…"
              : hasPendingRequest
              ? "Meeting request pending…"
              : "Request a meeting with my manager"}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Career Path Tab (own profile — read-only view) ───────────────────────────

const CAREER_PAGE_SIZE = 5;

export function CareerPathTab({
  employeeId: _employeeId,
  onRequestMeeting,
  isSending,
  hasPendingRequest,
}: {
  employeeId: number | null;
  onRequestMeeting?: () => void;
  isSending?: boolean;
  hasPendingRequest?: boolean;
}) {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);

  const { data: entries = [], isLoading } = useQuery<CareerPath[]>({
    queryKey: ["career-path", "my"],
    queryFn: apiGetMyCareerPath,
  });

  const totalPages = Math.max(1, Math.ceil(entries.length / CAREER_PAGE_SIZE));
  const paginated = entries.slice((page - 1) * CAREER_PAGE_SIZE, page * CAREER_PAGE_SIZE);

  if (isLoading) {
    return (
      <div className="space-y-4 mt-6">
        {[1, 2].map((i) => <div key={i} className="h-32 bg-muted rounded-xl animate-pulse" />)}
      </div>
    );
  }

  return (
    <div className="space-y-4 mt-6">

      {/* Section header */}
      <div>
        <h2 className="text-lg font-semibold text-foreground">{t("performance.careerPath.title")}</h2>
        <p className="text-sm text-muted-foreground mt-0.5">{t("performance.careerPath.subtitle")}</p>
      </div>

      {/* Empty state */}
      {entries.length === 0 && (
        <EmptyState
          icon={GraduationCap}
          title={t("performance.careerPath.noEntries")}
          description={t("performance.careerPath.noEntriesDesc")}
          onRequestMeeting={onRequestMeeting}
          isSending={isSending}
          hasPendingRequest={hasPendingRequest}
        />
      )}

      {entries.length > 0 && (
        <Collapsible defaultOpen={true}>
          <CollapsibleTrigger asChild>
            <button className="flex w-full items-center justify-between group mb-1">
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                  {t("performance.careerPath.title")}
                </span>
                <span className="text-xs text-muted-foreground">
                  · {entries.length} {entries.length === 1 ? "entry" : "entries"}
                </span>
              </div>
              <ChevronRight className="h-4 w-4 text-muted-foreground transition-transform duration-200 group-data-[state=open]:rotate-90" />
            </button>
          </CollapsibleTrigger>

          <CollapsibleContent className="space-y-4 mt-3">
            {paginated.map((entry) => (
              <Card key={entry.id} className="shadow-card border-l-4 border-l-primary/50 animate-in fade-in slide-in-from-bottom-2 duration-300">
                <CardContent className="pt-5 pb-4 px-5">
                  <div className="flex items-start justify-between gap-4 mb-4">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-full bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center shrink-0">
                        <ArrowUpCircle className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <p className="font-semibold text-foreground text-base">{entry.targetPosition}</p>
                        {entry.targetDepartment && (
                          <p className="text-sm text-muted-foreground">{entry.targetDepartment}</p>
                        )}
                      </div>
                    </div>
                    {entry.targetTimeline && (
                      <Badge variant="outline" className="text-xs flex items-center gap-1 shrink-0">
                        <Calendar className="h-3 w-3" /> {entry.targetTimeline}
                      </Badge>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {entry.requiredSkills && (
                      <div className="rounded-lg bg-blue-50 dark:bg-blue-500/10 p-3">
                        <p className="text-xs font-semibold text-blue-700 dark:text-blue-400 mb-1.5 flex items-center gap-1.5">
                          <BookOpen className="h-3.5 w-3.5" /> {t("performance.careerPath.requiredSkills")}
                        </p>
                        <p className="text-sm text-blue-800 dark:text-blue-300 whitespace-pre-line">{entry.requiredSkills}</p>
                      </div>
                    )}
                    {entry.developmentActions && (
                      <div className="rounded-lg bg-amber-50 dark:bg-amber-500/10 p-3">
                        <p className="text-xs font-semibold text-amber-700 dark:text-amber-400 mb-1.5 flex items-center gap-1.5">
                          <Lightbulb className="h-3.5 w-3.5" /> {t("performance.careerPath.developmentActions")}
                        </p>
                        <p className="text-sm text-amber-800 dark:text-amber-300 whitespace-pre-line">{entry.developmentActions}</p>
                      </div>
                    )}
                    {entry.notes && (
                      <div className="rounded-lg bg-muted/50 p-3 md:col-span-2">
                        <p className="text-xs font-semibold text-muted-foreground mb-1">{t("performance.careerPath.notes")}</p>
                        <p className="text-sm text-foreground whitespace-pre-line">{entry.notes}</p>
                      </div>
                    )}
                    {entry.outcome && (
                      <div className="rounded-lg border p-3 md:col-span-2">
                        <p className="text-xs font-semibold text-muted-foreground mb-2 uppercase tracking-wide">{t("performance.careerPath.outcomeSection")}</p>
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${
                            entry.outcome === "achieved" ? "bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-500/15 dark:text-emerald-400" :
                            entry.outcome === "not_achieved" ? "bg-red-100 text-red-700 border-red-200 dark:bg-red-500/15 dark:text-red-400" :
                            entry.outcome === "in_progress" ? "bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-500/15 dark:text-blue-400" :
                            "bg-zinc-100 text-zinc-600 border-zinc-200 dark:bg-zinc-500/15 dark:text-zinc-400"
                          }`}>
                            {t(`performance.careerPath.outcomes.${entry.outcome}`, entry.outcome)}
                          </span>
                          {entry.outcomeDate && (
                            <span className="text-xs text-muted-foreground">{new Date(entry.outcomeDate).toLocaleDateString()}</span>
                          )}
                        </div>
                        {entry.outcomeNotes && <p className="text-sm text-foreground whitespace-pre-line">{entry.outcomeNotes}</p>}
                        {entry.outcomeSetByName && (
                          <p className="text-[11px] text-muted-foreground mt-1 flex items-center gap-1">
                            <User className="h-3 w-3" /> {t("performance.careerPath.outcomeSetBy")}: {entry.outcomeSetByName}
                          </p>
                        )}
                      </div>
                    )}
                  </div>

                  {entry.setByName && (
                    <p className="text-[11px] text-muted-foreground mt-3 flex items-center gap-1">
                      <User className="h-3 w-3" />
                      {t("performance.careerPath.setBy")}: {entry.setByName} · {new Date(entry.createdAt).toLocaleDateString()}
                    </p>
                  )}
                </CardContent>
              </Card>
            ))}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between pt-1">
                <span className="text-xs text-muted-foreground">
                  Page {page} of {totalPages} · {entries.length} total
                </span>
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost" size="sm" className="h-7 w-7 p-0"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  >
                    <ChevronRight className="h-3.5 w-3.5 rotate-180" />
                  </Button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                    <Button
                      key={p}
                      variant={p === page ? "default" : "ghost"}
                      size="sm"
                      className={`h-7 w-7 p-0 text-xs ${p === page ? "" : "text-muted-foreground"}`}
                      onClick={() => setPage(p)}
                    >
                      {p}
                    </Button>
                  ))}
                  <Button
                    variant="ghost" size="sm" className="h-7 w-7 p-0"
                    disabled={page >= totalPages}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  >
                    <ChevronRight className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            )}
          </CollapsibleContent>
        </Collapsible>
      )}
    </div>
  );
}

export default Performance;
